/**
 * "The policy runs a year from the effective date", which it almost always does.
 *
 * Josh, 2026-09-06: "could we have the expiration date automatically populate 1
 * year after the effective? 99.9% of these policies will be 12 month policies.
 * If they're not, they can manually change."
 *
 * ⚠️ NO Date PARSING, AND THAT IS NOT FUSSINESS. `new Date('2026-09-24')` parses
 * as UTC midnight, which is the 23rd for everybody west of Greenwich — so the
 * obvious implementation quietly hands a carrier a policy period off by a day.
 * LossRuns.tsx already carries a warning about exactly this bug in
 * policyPeriodText; this is the same trap one function along. The arithmetic is
 * done on the string parts, where a day cannot move.
 *
 * ⚠️ AND THE 29TH OF FEBRUARY CLAMPS RATHER THAN ROLLING OVER. A policy
 * incepting 29/02/2028 expires 28/02/2029, not the 1st of March: an extra day
 * of coverage nobody bought is a worse default than a day short, and rolling
 * into the next month is the kind of thing that reads as a bug on a dec page.
 */

/** Days in a month, 1-indexed. Only February is interesting. */
const daysInMonth = (year: number, month: number): number =>
  month === 2 ? (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28) : [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;

/**
 * One year on from an `<input type="date">` value.
 *
 * Returns '' for anything that is not a complete YYYY-MM-DD, so a half-typed
 * date fills nothing rather than filling something wrong.
 */
export function oneYearAfter(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? '').trim());
  if (!m) return '';
  const year = Number(m[1]) + 1;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) return '';
  const clamped = Math.min(day, daysInMonth(year, month));
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(clamped).padStart(2, '0')}`;
}
